<template>
  <Header/>
  <div class="container is-max-desktop my-bookings">
    <h1>Administrator dashboard</h1>

    <h2>Opret bruger</h2>
    <p>
      Angiv e-mail og husstand. Brugeren kan logge ind med Google.
      Afkryds valgfrit «Tillad også login med Mail + kode» og angiv en kode.
    </p>

    <form class="form-section max-width" @submit.prevent="createUser">
      <div class="field">
        <label class="label" for="admin-email">E-mail</label>
        <div class="control">
          <input
            id="admin-email"
            v-model="form.email"
            class="input"
            type="email"
            required
            autocomplete="off"
          >
        </div>
      </div>
      <div class="field">
        <label class="label" for="admin-account">Husstand</label>
        <div class="control">
          <div class="select is-fullwidth">
            <select id="admin-account" v-model.number="form.account" required>
              <option disabled :value="0">Vælg husstand</option>
              <option v-for="account in accounts" :key="account.id" :value="account.id">
                {{ account.name }}
              </option>
            </select>
          </div>
        </div>
      </div>
      <div class="field">
        <label class="label" for="admin-display-name">Visningsnavn (valgfrit)</label>
        <div class="control">
          <input
            id="admin-display-name"
            v-model="form.displayName"
            class="input"
            type="text"
            autocomplete="off"
          >
        </div>
      </div>
      <div class="field">
        <label class="checkbox">
          <input v-model="form.allowPasswordLogin" type="checkbox">
          Tillad også login med Mail + kode
        </label>
        <p class="help">
          Google-login virker altid. Med afkrydsning kan brugeren også logge ind med mail + kode.
        </p>
      </div>
      <div v-if="form.allowPasswordLogin" class="field">
        <label class="label" for="admin-password">Kode</label>
        <div class="control">
          <input
            id="admin-password"
            v-model="form.password"
            class="input"
            type="text"
            required
            minlength="4"
            autocomplete="new-password"
            placeholder="Mindst 4 tegn"
          >
        </div>
      </div>
      <div class="field">
        <label class="checkbox">
          <input v-model="form.isAdmin" type="checkbox">
          Administrator
        </label>
      </div>
      <div v-if="formError" class="notification is-danger">{{ formError }}</div>
      <div v-if="formSuccess" class="notification is-success">{{ formSuccess }}</div>
      <button class="button is-success" type="submit" :disabled="saving">
        {{ saving ? 'Opretter…' : 'Opret bruger' }}
      </button>
    </form>

    <h2>Brugere</h2>
    <div v-if="users.length > 0" class="table-container">
      <table class="table is-bordered is-striped is-narrow is-hoverable is-fullwidth">
        <thead>
          <tr>
            <th>E-mail</th>
            <th>Navn</th>
            <th>Husstand</th>
            <th>Admin</th>
            <th>Google (Clerk)</th>
            <th>Mail + kode</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="user in users" :key="user.id">
            <td>{{ user.email }}</td>
            <td>{{ user.displayName }}</td>
            <td>{{ accountName(user.account) }}</td>
            <td>{{ user.accessLvl === 1 ? 'Ja' : '' }}</td>
            <td>{{ user.hasClerk ? 'Ja' : '' }}</td>
            <td>{{ user.hasPassword ? 'Ja' : '' }}</td>
          </tr>
        </tbody>
      </table>
    </div>
    <p v-else>Ingen brugere hentet.</p>

    <LoginStatus />
  </div>
  <Footer/>
</template>

<script>
import ax from 'axios'
export const axios = ax

import { defineComponent } from 'vue'
import { useComposition } from '@/compositions/composition'

import Header from '@/components/Header.vue'
import Footer from '@/components/Footer.vue'
import LoginStatus from '@/components/LoginStatus.vue'

export default defineComponent({
  setup() {
    const { global } = useComposition();
    return {
        global,
        siteRoot: process.env.VUE_APP_SITE_ROOT,
        apiRoot: process.env.VUE_APP_API_ROOT
    }
  },
  data() {
    return {
      accounts: [],
      users: [],
      saving: false,
      formError: '',
      formSuccess: '',
      form: {
        email: '',
        account: 0,
        displayName: '',
        allowPasswordLogin: false,
        password: '',
        isAdmin: false
      }
    }
  },
  components: {
    Header,
    Footer,
    LoginStatus,
  },
  created() {
      this.loadAdminData();
  },
  methods: {
      accountName(accountId) {
        const account = this.accounts.find((row) => row.id === accountId);
        return account ? account.name : accountId;
      },
      loadAdminData() {
        axios.get(process.env.VUE_APP_API_ROOT + '/users-admin.php')
        .then(response => {
            this.accounts = response.data.accounts || [];
            this.users = response.data.users || [];
        })
        .catch((error) => {
          console.error('Kunne ikke hente brugere:', error);
        });
      },
      createUser() {
        this.formError = '';
        this.formSuccess = '';
        if (this.form.allowPasswordLogin && String(this.form.password).length < 4) {
          this.formError = 'Angiv en kode på mindst 4 tegn.';
          return;
        }
        this.saving = true;
        axios.post(process.env.VUE_APP_API_ROOT + '/users-admin.php', {
          email: this.form.email,
          account: this.form.account,
          displayName: this.form.displayName,
          accessLvl: this.form.isAdmin ? 1 : 0,
          loginType: this.form.allowPasswordLogin ? 'password' : 'google',
          password: this.form.allowPasswordLogin ? this.form.password : ''
        })
        .then((response) => {
          if (response.data && response.data.success) {
            const loginHint = response.data.user.hasPassword
              ? ' (Google + mail/kode)'
              : ' (Google)';
            this.formSuccess = 'Bruger oprettet: ' + response.data.user.email + loginHint;
            this.form.email = '';
            this.form.displayName = '';
            this.form.password = '';
            this.form.allowPasswordLogin = false;
            this.form.isAdmin = false;
            this.loadAdminData();
          } else {
            this.formError = (response.data && response.data.friendly && response.data.friendly.da)
              || 'Kunne ikke oprette bruger.';
          }
        })
        .catch((error) => {
          const data = error.response && error.response.data;
          this.formError = (data && data.friendly && data.friendly.da)
            || 'Kunne ikke oprette bruger.';
        })
        .finally(() => {
          this.saving = false;
        });
      }
  }
})
</script>
